const _clusterArray = (items: string[], masterData: any): string[] => {
  if (!items || items.length === 0) return [];
  
  let clustersData: any[] = [];
  if (Array.isArray(masterData)) {
    clustersData = masterData;
  } else if (masterData && Array.isArray(masterData.designations)) {
    clustersData = masterData.designations;
  } else if (masterData && Array.isArray(masterData.data)) {
    clustersData = masterData.data;
  }

  if (clustersData.length === 0) return items;

  const result: string[] = [];
  const itemsSet = new Set(items);
  const subsumedItems = new Set<string>();

  clustersData.forEach((item) => {
    // Format 1: raw API format with categoryName and categoryChildNames
    if (item.categoryName && Array.isArray(item.categoryChildNames) && item.categoryChildNames.length > 0) {
      const clusterChildNames = item.categoryChildNames;
      const isCompletelySelected = clusterChildNames.every((childName: string) => itemsSet.has(childName));

      if (isCompletelySelected) {
        result.push(item.categoryName);
        clusterChildNames.forEach((childName: string) => subsumedItems.add(childName));
      }
    } 
    // Format 2: generic tree format with name and children
    else if (item.name && Array.isArray(item.children) && item.children.length > 0) {
      const clusterChildNames = item.children.map((child: any) => child.name);
      const isCompletelySelected = clusterChildNames.every((childName: string) => itemsSet.has(childName));

      if (isCompletelySelected) {
        result.push(item.name);
        clusterChildNames.forEach((childName: string) => subsumedItems.add(childName));
      }
    }
  });

  items.forEach((item) => {
    if (!subsumedItems.has(item)) {
      result.push(item);
    }
  });

  // Deduplicate in case raw array repeats the same categoryName
  return Array.from(new Set(result));
};

const _removeFromArray = (items: string[], removedItem: string, masterData: any): string[] => {
  if (!items || items.length === 0) return [];
  if (!removedItem) return items;

  let clustersData: any[] = [];
  if (Array.isArray(masterData)) {
    clustersData = masterData;
  } else if (masterData && Array.isArray(masterData.designations)) {
    clustersData = masterData.designations;
  } else if (masterData && Array.isArray(masterData.data)) {
    clustersData = masterData.data;
  }

  if (clustersData.length === 0) {
    return items.filter((item) => item !== removedItem);
  }

  // Find all items that match the removed cluster name
  const clusterMatches = clustersData.filter(
    (c: any) => c.categoryName === removedItem || c.name === removedItem
  );

  if (clusterMatches.length > 0) {
    const clusterChildNamesSet = new Set<string>();
    clusterMatches.forEach((cluster) => {
      if (Array.isArray(cluster.categoryChildNames)) {
        cluster.categoryChildNames.forEach((child: string) => clusterChildNamesSet.add(child));
      } else if (Array.isArray(cluster.children)) {
        cluster.children.forEach((child: any) => clusterChildNamesSet.add(child.name));
      }
    });

    if (clusterChildNamesSet.size > 0) {
      return items.filter((item) => !clusterChildNamesSet.has(item));
    }
  }

  return items.filter((item) => item !== removedItem);
};

/**
 * Processes a full designation object, returning clustered string arrays for designations.
 * @param designationObj - The designation state object containing designations array
 * @param masterData - Master cluster data
 * @returns Object with clustered arrays: { designations: string[] }
 */
export const getClusteredDesignations = (designationObj: any, masterData: any) => {
  if (!designationObj) return { designations: [] };
  
  return {
    designations: _clusterArray(designationObj.designations || [], masterData)
  };
};

/**
 * Removes a clustered or standalone designation item from a full designation state object.
 * 
 * @param designationObj - The designation state object
 * @param removedItem - The string to remove
 * @param masterData - Master cluster data
 * @returns An object containing the fully updated designationObj, and the new clustered string arrays.
 */
export const removeDesignationOrCluster = (
  designationObj: any, 
  removedItem: string, 
  masterData: any
) => {
  const updated = { ...designationObj };
  
  updated.designations = _removeFromArray(updated.designations || [], removedItem, masterData);

  const clusteredStrs = getClusteredDesignations(updated, masterData);
  return {
    updatedDesignations: updated,
    designations: clusteredStrs.designations
  };
};
